import React, { useEffect, useMemo, useState } from 'react';
import './BuyerBasket.css';
import { siftApiClient } from '../../api/siftApiClient';
import type { BrokerBasketSuggestion, OutreachDraftResponse } from '../../api/types';
import { BASKET_WIDTH_KEY, BASKET_DEFAULT_WIDTH, clampBasketWidth } from './basketWidth';
import { toVinGroups, overlappingBuyers, toOutreachGroups, type BuyerOverlap } from './basketGroups';
import { BuyerPicker } from './BuyerPicker';
import { DistanceTolerance } from './DistanceTolerance';
import { toSalesforceBlocks } from './salesforceBlocks';

/** One VIN staged for outreach to a buyer. */
export interface BasketVin {
  vin: string;
  label: string;
  seller?: string | null;
  seller_id?: string | null;
  seller_loc?: string | null;
}

/** Minimal buyer identity the basket needs (both match- and board-buyers fit). */
export interface BasketBuyerInfo {
  buyer_uid: string;
  buyer_name: string | null;
  buyer_zip_code: string | null;
  state: string | null;
}

/** A buyer plus the VINs a broker has staged to pitch to them. */
export interface BasketBuyer {
  buyer: BasketBuyerInfo;
  vins: BasketVin[];
}

/** How the staged outreach work is organized in the panel. */
type GroupBy = 'buyer' | 'seller' | 'vin';

/** One draft inside a batch run. `id` is the buyer uid, the VIN, or the cluster
 *  key, depending on which run produced it. */
interface BatchDraftResult {
  id: string;
  name: string;
  subject: string;
  body: string;
  vinLinks: Record<string, string>;
  error: string | null;
  /** VINs this buyer had no match or engagement on, so those lines read generic. */
  unpersonalized?: string[];
  /** Price statements that contradicted the vehicle data. */
  priceWarnings?: string[];
  /** Who to send this one to. Resolved for every buyer-addressed workflow;
   *  absent only for seller drafts, which have no address source. */
  recipients?: { emails: string[]; noEmail: BasketBuyerInfo[] };
}

/** Progress + results for a batch draft run (one email per buyer, or per VIN). */
interface BatchDraftState {
  running: boolean;
  total: number;
  done: number;
  results: BatchDraftResult[];
  /** Which draft was last copied — outreach is sent one at a time. */
  copiedUid: string | null;
  title: string;
  /** Buyers who will receive more than one of these emails (§2.0). Empty for
   *  per-buyer runs, which cannot duplicate by construction. */
  overlap: BuyerOverlap[];
}

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));

/**
 * Turn every VIN in a draft body into a link to its VDP.
 *
 * The model is instructed never to write URLs, so the anchors are built here
 * from our own VIN -> link map: the broker gets a clickable VIN rather than a
 * bare address, and the link can never be hallucinated or mangled in prose.
 */
function draftToHtml(body: string, links: Record<string, string>): string {
  let html = escapeHtml(body);
  for (const [vin, url] of Object.entries(links)) {
    const safeVin = vin.replace(/[^A-Za-z0-9]/g, '');
    if (!safeVin) continue;
    html = html.replace(
      new RegExp(`\\b${safeVin}\\b`, 'gi'),
      `<a href="${escapeHtml(url)}">${escapeHtml(vin)}</a>`,
    );
  }
  return `<div>${html.replace(/\n/g, '<br>')}</div>`;
}

/**
 * Copy a draft as rich text so the VIN anchors survive the paste, with a
 * plain-text alternative that lists the links instead — a text-only paste would
 * otherwise drop them entirely.
 */
async function copyDraftToClipboard(
  subject: string, body: string, links: Record<string, string>,
  recipients?: { emails: string[]; noEmail: BasketBuyerInfo[] },
): Promise<void> {
  const linkLines = Object.entries(links).map(([vin, url]) => `${vin}: ${url}`);
  // Semicolon-joined so it pastes straight into a To/BCC field.
  const toLine = recipients?.emails.length ? `To: ${recipients.emails.join('; ')}\n\n` : '';
  const noEmailLines = recipients?.noEmail.length
    ? `No email on file — contact manually:\n${recipients.noEmail
        .map((b) => `  ${b.buyer_uid}${b.buyer_name ? ` — ${b.buyer_name}` : ''}`)
        .join('\n')}\n\n`
    : '';
  const plain = [
    toLine,
    noEmailLines,
    subject ? `Subject: ${subject}\n\n` : '',
    body,
    linkLines.length ? `\n\n${linkLines.join('\n')}` : '',
  ].join('');
  const html = (toLine ? `<div><b>To:</b> ${escapeHtml(recipients!.emails.join('; '))}</div><br>` : '')
    + (noEmailLines ? `<div>${escapeHtml(noEmailLines).replace(/\n/g, '<br>')}</div>` : '')
    + (subject ? `<div><b>Subject:</b> ${escapeHtml(subject)}</div><br>` : '')
    + draftToHtml(body, links);
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      })]);
      return;
    }
  } catch {
    /* rich copy unsupported or blocked — fall back to plain text */
  }
  await navigator.clipboard.writeText(plain);
}

/** One staged VIN pivoted under its seller, keeping the buyer it's going to. */
interface SellerVinItem {
  vin: string;
  label: string;
  buyer: BasketBuyerInfo;
}

/** A seller plus the staged VINs of theirs and which buyer each is pitched to. */
interface SellerGroup {
  key: string;
  seller: string | null;
  sellerId: string | null;
  sellerLoc: string | null;
  items: SellerVinItem[];
}

/** Price statements the server could not reconcile with the vehicle data.
 *  An alert, not a note: these are the errors that stop a seller trusting us. */
const PriceWarnings: React.FC<{ warnings?: string[] }> = ({ warnings }) =>
  warnings && warnings.length > 0 ? (
    <div className="bb-draft-warning" role="alert">
      <strong>Check pricing before sending.</strong> These statements don't match the vehicle data:
      <ul className="bb-draft-warning-list">
        {warnings.map((w) => <li key={w}>{w}</li>)}
      </ul>
    </div>
  ) : null;

/** Transient state for the AI outreach-draft modal (§9.8; generate, not send). */
interface DraftState {
  open: boolean;
  loading: boolean;
  mode: 'buyer' | 'seller' | 'generic';
  title: string;
  error: string | null;
  result: OutreachDraftResponse | null;
  copied: boolean;
  recipients?: { emails: string[]; noEmail: BasketBuyerInfo[] };
}

/** Re-pivot the buyer-grouped basket into seller groups (client-side only). */
function toSellerGroups(buyers: BasketBuyer[]): SellerGroup[] {
  const map = new Map<string, SellerGroup>();
  for (const { buyer, vins } of buyers) {
    for (const v of vins) {
      const key = v.seller_id || v.seller || 'unknown';
      let g = map.get(key);
      if (!g) {
        g = { key, seller: v.seller ?? null, sellerId: v.seller_id ?? null, sellerLoc: v.seller_loc ?? null, items: [] };
        map.set(key, g);
      }
      g.items.push({ vin: v.vin, label: v.label, buyer });
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    (a.seller || a.key).localeCompare(b.seller || b.key));
}

interface BuyerBasketProps {
  open: boolean;
  buyers: BasketBuyer[];
  onClose: () => void;
  onRemoveVin: (buyerUid: string, vin: string) => void;
  onRemoveBuyer: (buyerUid: string) => void;
  onClear: () => void;
  /** Re-open a staged VIN's details (brings its seller into the carousel too). */
  onOpenVin?: (vin: string, sellerId?: string | null) => void;
  /** Jump to a staged VIN's seller card. */
  onOpenSeller?: (sellerName?: string | null, sellerId?: string | null) => void;
  /** Jump to a staged buyer on the Buyer tab. */
  onOpenBuyer?: (buyerUid: string, buyerName?: string | null) => void;
  /** Stage a suggested unit under the same buyer. Section hidden when omitted. */
  onAddSuggestion?: (buyer: BasketBuyerInfo, s: BrokerBasketSuggestion) => void;
  /** §2.6 — stage an already-basketed VIN for another buyer (copy, not move). */
  onAssignVinToBuyer?: (buyer: BasketBuyerInfo, vin: BasketVin) => void;
}

/**
 * "Also from these sellers" — more units this buyer could take, drawn from the
 * sellers already in their basket. Uses the full match pool, so a unit where the
 * buyer ranks #10 or #12 still shows: the conversation is already open, so a
 * looser cohort fit is still worth pitching (Tara's "no dead ends", 8/5).
 */
const BasketSuggestions: React.FC<{
  buyer: BasketBuyerInfo;
  stagedVins: BasketVin[];
  /** Every basket buyer holding each VIN, so a suggestion can say whether it is
   *  a fresh recommendation or a unit already staged under someone else. */
  buyersByVin: Map<string, BasketBuyerInfo[]>;
  onAdd: (buyer: BasketBuyerInfo, s: BrokerBasketSuggestion) => void;
  onOpenVin?: (vin: string, sellerId?: string | null) => void;
}> = ({ buyer, stagedVins, buyersByVin, onAdd, onOpenVin }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<BrokerBasketSuggestion[] | null>(null);
  const [radius, setRadius] = useState<number | undefined>(undefined);
  // Trails `radius` by a beat so sweeping the slider is one query, not forty.
  const [debouncedRadius, setDebouncedRadius] = useState<number | undefined>(undefined);
  // Board-wide by default: restricting to the basket's sellers came back empty
  // for ~54% of one-VIN baskets, because one seller rarely holds several units
  // in the same buyer's cohort.
  const [scope, setScope] = useState<'any' | 'sellers'>('any');

  // Keyed as strings, not arrays: the basket hands down a fresh `vins` array on
  // every mutation, so an array dep would refetch this buyer's suggestions when
  // an unrelated buyer changed.
  const sellerKey = useMemo(
    () => Array.from(new Set(stagedVins.map((v) => v.seller_id).filter((s): s is string => !!s)))
      .sort()
      .join(','),
    [stagedVins],
  );
  const stagedKey = useMemo(() => stagedVins.map((v) => v.vin).sort().join(','), [stagedVins]);

  // The server excludes these too, but it answers a request built from an older
  // render: stage a VIN while the panel is open and the in-flight response still
  // contains it. Filtering here makes "already in this buyer's basket" impossible
  // to display regardless of timing.
  const stagedHere = useMemo(
    () => new Set(stagedVins.map((v) => v.vin.trim().toUpperCase())),
    [stagedVins],
  );
  const visible = useMemo(
    () => (items ?? []).filter((s) => !stagedHere.has(String(s.vin).trim().toUpperCase())),
    [items, stagedHere],
  );

  useEffect(() => {
    if (radius === debouncedRadius) return;
    const t = setTimeout(() => setDebouncedRadius(radius), 200);
    return () => clearTimeout(t);
  }, [radius, debouncedRadius]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await siftApiClient.getBrokerBasketSuggestions({
          buyer_uid: buyer.buyer_uid,
          seller_ids: sellerKey,
          scope,
          exclude_vins: stagedKey || undefined,
          limit: 12,
          max_distance_miles: debouncedRadius,
        });
        if (!cancelled) setItems(res.suggestions ?? []);
      } catch {
        if (!cancelled) setError('Could not load more inventory for this buyer.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, buyer.buyer_uid, sellerKey, stagedKey, debouncedRadius, scope]);

  const sellerCount = sellerKey ? sellerKey.split(',').length : 0;

  return (
    <div className="bb-suggest">
      <button
        type="button"
        className="bb-suggest-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} More for this buyer
      </button>
      {open && (
        <div className="bb-suggest-body">
          <div className="bb-suggest-controls">
            <div className="bb-suggest-ctlrow">
              <span className="bb-suggest-ctllabel">From</span>
              <button
                type="button"
                className={`bb-suggest-radius${scope === 'any' ? ' on' : ''}`}
                onClick={() => setScope('any')}
                title="Search the whole board for this buyer, preferring sellers already in the basket"
              >Any seller</button>
              <button
                type="button"
                className={`bb-suggest-radius${scope === 'sellers' ? ' on' : ''}`}
                onClick={() => setScope('sellers')}
                disabled={sellerCount === 0}
                title={sellerCount === 0
                  ? 'No seller recorded on the staged VINs'
                  : 'Only units from the sellers already in this basket'}
              >{sellerCount === 1 ? 'This seller' : 'Basket sellers'}</button>
            </div>
            <div className="bb-suggest-ctlrow">
              <DistanceTolerance
                id={`bb-suggest-radius-${buyer.buyer_uid}`}
                label="Within"
                value={radius ?? 0}
                onChange={(m) => setRadius(m ? m : undefined)}
                title="Keep the bundle transportable"
              />
            </div>
          </div>
          {loading && <div className="bb-suggest-note">Looking for more units…</div>}
          {error && <div className="bb-suggest-note bb-suggest-error">{error}</div>}
          {!loading && !error && items && visible.length === 0 && (
            <div className="bb-suggest-note">
              {scope === 'sellers'
                ? `Nothing else from ${sellerCount === 1 ? 'this seller' : 'these sellers'} fits this buyer${radius ? ' within that radius' : ''}. Try "Any seller".`
                : `No other board inventory fits this buyer${radius ? ' within that radius' : ''}.`}
            </div>
          )}
          {!loading && visible.length > 0 && (
            <ul className="bb-suggest-list">
              {visible.map((s) => {
                // Anything reaching here is not staged for THIS buyer (the guard
                // above removed those), so every holder is someone else.
                const alsoStagedFor = buyersByVin.get(String(s.vin).trim().toUpperCase()) ?? [];
                return (
                <li key={s.vin} className="bb-suggest-item">
                  <span className="bb-suggest-info">
                    <span className="bb-suggest-titlerow">
                      {onOpenVin ? (
                        <button
                          type="button"
                          className="bb-link bb-vin-label"
                          onClick={() => onOpenVin(s.vin, s.seller_id)}
                          title={`Open VIN details — ${s.vin}`}
                        >
                          {[s.model_year, s.make, s.model].filter(Boolean).join(' ') || s.vin}
                        </button>
                      ) : (
                        <span className="bb-vin-label">
                          {[s.model_year, s.make, s.model].filter(Boolean).join(' ') || s.vin}
                        </span>
                      )}
                      {alsoStagedFor.length > 0 ? (
                        <span
                          className="bb-suggest-tag is-staged"
                          title={`Already staged for ${alsoStagedFor
                            .map((b) => b.buyer_name || b.buyer_uid).join(', ')}`}
                        >
                          In basket · {alsoStagedFor.length === 1
                            ? (alsoStagedFor[0].buyer_name || `Buyer ${alsoStagedFor[0].buyer_uid}`)
                            : `${alsoStagedFor.length} other buyers`}
                        </span>
                      ) : (
                        <span className="bb-suggest-tag is-new" title="Not staged for anyone yet">
                          New
                        </span>
                      )}
                    </span>
                    <span className="bb-suggest-meta">
                      {[
                        // Year/make/model alone is not an identity: ~29% of board
                        // units share one with another unit from the same seller,
                        // so without these the rows read as duplicates.
                        s.mileage != null ? `${Math.round(s.mileage / 1000)}k mi` : null,
                        s.cr_grade != null ? `CR ${s.cr_grade.toFixed(1)}` : null,
                        s.price != null ? `$${Math.round(s.price).toLocaleString()}` : null,
                        s.vin ? `…${s.vin.slice(-6)}` : null,
                        // Names the SELLER, not the vehicle — the old "✓ in basket"
                        // read as though the unit itself were already staged.
                        s.seller_in_basket ? 'seller already in basket' : null,
                        s.seller_name,
                        s.fit_score != null ? `fit ${Math.round(s.fit_score)}` : null,
                        s.buyer_rank != null ? `#${s.buyer_rank} for this buyer` : null,
                        s.distance_miles != null ? `${Math.round(s.distance_miles)} mi` : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="bb-suggest-add"
                    onClick={() => onAdd(buyer, s)}
                    title="Stage this unit for the same buyer"
                  >+</button>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The outreach basket. As a broker reviews stalled VINs and adds matched
 * buyers, the basket accumulates them grouped BY BUYER — so a broker can pitch
 * several vehicles to one contact in a single reach-out (the batch-per-contact
 * workflow). Outreach send (SES) is a later phase; this stages the work.
 */
export const BuyerBasket: React.FC<BuyerBasketProps> = ({
  open,
  buyers,
  onClose,
  onRemoveVin,
  onRemoveBuyer,
  onClear,
  onOpenVin,
  onOpenSeller,
  onOpenBuyer,
  onAddSuggestion,
  onAssignVinToBuyer,
}) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('buyer');
  // Drag-resizable width, remembered per device — the fixed 380px was too tight
  // once email drafts and suggestions shared the panel.
  const [width, setWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(BASKET_WIDTH_KEY));
    return clampBasketWidth(Number.isFinite(saved) && saved > 0 ? saved : BASKET_DEFAULT_WIDTH);
  });
  // A width saved on a wide monitor used to survive onto a narrow one, where
  // max-width:95vw shrank the box but not the header laid out for the old width,
  // pushing the close button off-screen.
  useEffect(() => {
    const onResize = () => setWidth((w) => clampBasketWidth(w));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: MouseEvent) => {
      setWidth(clampBasketWidth(startWidth + (startX - ev.clientX)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setWidth((w) => {
        localStorage.setItem(BASKET_WIDTH_KEY, String(w));
        return w;
      });
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const sellerGroups = useMemo(() => toSellerGroups(buyers), [buyers]);
  const vinGroups = useMemo(() => toVinGroups(buyers), [buyers]);
  // Which basket buyers already hold each VIN — lets a suggestion say whether it
  // is new or already staged under someone else.
  const buyersByVin = useMemo(
    () => new Map(vinGroups.map((g) => [g.vin.trim().toUpperCase(), g.buyers])),
    [vinGroups],
  );

  // Distance is a property of the (buyer, VIN) PAIR, so it cannot be stamped on
  // the staged VIN: the basket stages one VIN object under several buyers, and the
  // same unit measured 1,011 miles from one buyer and 17 from another. Resolved
  // here instead, which also fills in baskets staged before this existed.
  const [distances, setDistances] = useState<Map<string, number>>(new Map());
  const pairKey = useMemo(
    () => buyers
      .flatMap((b) => b.vins.map((v) => `${b.buyer.buyer_uid}:${v.vin.trim().toUpperCase()}`))
      .sort()
      .join(','),
    [buyers],
  );
  useEffect(() => {
    if (!open || !pairKey) { setDistances(new Map()); return; }
    const controller = new AbortController();
    const pairs = pairKey.split(',').map((p) => {
      const [buyer_uid, vin] = p.split(':');
      return { buyer_uid, vin };
    });
    (async () => {
      try {
        const res = await siftApiClient.getBrokerBasketDistances(pairs, controller.signal);
        const next = new Map<string, number>();
        for (const d of res.distances) {
          if (d.distance_miles != null) {
            next.set(`${d.buyer_uid}:${String(d.vin).trim().toUpperCase()}`, d.distance_miles);
          }
        }
        setDistances(next);
      } catch {
        // Distance annotates the row; a failure must not empty the basket.
      }
    })();
    return () => controller.abort();
  }, [open, pairKey]);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [copyingContacts, setCopyingContacts] = useState(false);
  const [openDraftUid, setOpenDraftUid] = useState<string | null>(null);
  const [contactsMsg, setContactsMsg] = useState<string | null>(null);
  const [batch, setBatch] = useState<BatchDraftState | null>(null);
  // Escape is the fallback way out: the close button has been unreachable, and
  // there is no overlay to click. Unwinds the innermost layer first so it never
  // discards an open draft along with the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (batch) setBatch(null);
      else if (draft) setDraft(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, batch, draft, onClose]);
  if (!open) return null;
  const totalVins = buyers.reduce((n, b) => n + b.vins.length, 0);

  /** Addresses for a set of buyers. Additive: a failure still leaves a usable
   *  draft, so it resolves to no addresses rather than throwing. */
  const resolveRecipients = async (
    groups: BasketBuyerInfo[][],
  ): Promise<{ emails: string[]; noEmail: BasketBuyerInfo[] }[]> => {
    const uids = [...new Set(groups.flat().map((b) => b.buyer_uid))];
    let emails: Record<string, string> = {};
    if (uids.length) {
      try {
        emails = await siftApiClient.getBrokerBuyerEmails(uids);
      } catch {
        /* additive — the draft and the account numbers are still usable */
      }
    }
    return groups.map((g) => ({
      emails: g.map((b) => emails[b.buyer_uid]).filter((e): e is string => !!e),
      noEmail: g.filter((b) => !emails[b.buyer_uid]),
    }));
  };

  const runDraft = async (
    mode: 'buyer' | 'seller' | 'generic',
    title: string,
    req: Parameters<typeof siftApiClient.draftBrokerOutreach>[0],
    recipientBuyers?: BasketBuyerInfo[],
  ) => {
    if (!req.vins.length) return;
    setDraft({ open: true, loading: true, mode, title, error: null, result: null, copied: false });
    try {
      const [result, recipients] = await Promise.all([
        siftApiClient.draftBrokerOutreach(req),
        recipientBuyers ? resolveRecipients([recipientBuyers]).then((r) => r[0]) : undefined,
      ]);
      setDraft({ open: true, loading: false, mode, title, error: null, result, copied: false, recipients });
    } catch {
      setDraft({
        open: true, loading: false, mode, title, result: null, copied: false,
        error: 'Could not generate a draft right now. Please try again.',
      });
    }
  };

  // Draft a batch of emails (Jordan asked for 20+ at a time). Runs a small
  // concurrency window rather than firing every request at once, so a big basket
  // doesn't hammer Bedrock or stall the browser's connection pool.
  const runBatch = async (
    title: string,
    targets: {
      id: string;
      name: string;
      req: Parameters<typeof siftApiClient.draftBrokerOutreach>[0];
      recipients?: BatchDraftResult['recipients'];
    }[],
    overlap: BuyerOverlap[],
  ) => {
    if (targets.length === 0) return;
    setBatch({ running: true, total: targets.length, done: 0, results: [], copiedUid: null, title, overlap });
    const results: BatchDraftResult[] = new Array(targets.length);
    let cursor = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const i = cursor++;
        const t = targets[i];
        try {
          const res = await siftApiClient.draftBrokerOutreach(t.req);
          results[i] = {
            id: t.id, name: t.name, subject: res.subject, body: res.body,
            vinLinks: res.vin_links ?? {}, error: null, recipients: t.recipients,
            unpersonalized: res.unpersonalized_vins ?? [],
            priceWarnings: res.validation_warnings ?? [],
          };
        } catch {
          results[i] = {
            id: t.id, name: t.name, subject: '', body: '',
            vinLinks: {}, error: 'Draft failed', recipients: t.recipients,
          };
        }
        setBatch((b) => (b ? { ...b, done: b.done + 1 } : b));
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, targets.length) }, worker));
    setBatch({ running: false, total: targets.length, done: targets.length, results, copiedUid: null, title, overlap });
    // Open the first draft so the panel lands on something actionable.
    setOpenDraftUid(results.find((r) => !r.error)?.id ?? null);
  };

  /** One personalised email per buyer, covering all of that buyer's staged VINs. */
  const runBatchDrafts = async () => {
    const staged = buyers.filter((b) => b.vins.length > 0);
    if (staged.length === 0) return;
    const recipients = await resolveRecipients(staged.map(({ buyer }) => [buyer]));
    await runBatch(
      'Buyer outreach drafts',
      staged.map(({ buyer, vins }, i) => ({
        id: buyer.buyer_uid,
        name: buyer.buyer_name || `Buyer ${buyer.buyer_uid}`,
        req: { mode: 'buyer' as const, buyer_uid: buyer.buyer_uid, vins: vins.map((v) => v.vin) },
        recipients: recipients[i],
      })),
      // A per-buyer run sends each buyer exactly one email, so it cannot duplicate.
      [],
    );
  };

  /**
   * §2.1 — one generic email per group of buyers staged against the same VINs,
   * each with the list of buyers to send it to. `toOutreachGroups` guarantees a
   * buyer appears in exactly one of them, which is why this run passes no overlap
   * warning: there is nothing to warn about by construction.
   */
  const runGenericDrafts = async () => {
    const groups = toOutreachGroups(buyers);
    if (groups.length === 0) return;
    const recipients = await resolveRecipients(groups.map((g) => g.buyers));
    await runBatch(
      'Generic emails by buyer group',
      groups.map((g, i) => ({
        id: g.key,
        name: `${g.vins.length} VIN${g.vins.length !== 1 ? 's' : ''} → ${g.buyers.length} buyer${g.buyers.length !== 1 ? 's' : ''}`,
        req: { mode: 'generic' as const, vins: g.vins.map((v) => v.vin) },
        recipients: recipients[i],
      })),
      [],
    );
  };

  /** §2.1: one generic email per VIN, for the broker to paste to that VIN's
   *  buyer set. Fewer drafts to write than the per-buyer run, at the cost of a
   *  buyer on two VINs hearing from us twice — hence the overlap warning. */
  const runVinBatchDrafts = async () => {
    if (vinGroups.length === 0) return;
    const recipients = await resolveRecipients(vinGroups.map((g) => g.buyers));
    await runBatch(
      'Per-VIN outreach drafts',
      vinGroups.map((g, i) => ({
        id: g.vin,
        name: g.label,
        req: { mode: 'generic' as const, vins: [g.vin] },
        recipients: recipients[i],
      })),
      overlappingBuyers(vinGroups),
    );
  };

  /**
   * The basket is where the outreach actually gets worked, so the buyer contact
   * block belongs here rather than on the board: these are the buyers a broker
   * has already committed to. Pastes into Sheets/Excel.
   */
  const copyBuyerBlock = async () => {
    if (buyers.length === 0) return;
    setCopyingContacts(true);
    try {
      let emails: Record<string, string> = {};
      try {
        emails = await siftApiClient.getBrokerBuyerEmails(
          buyers.map((b) => b.buyer.buyer_uid),
        );
      } catch {
        /* additive — still copy names, account numbers and VINs */
      }
      const text = [
        ['Buyer', 'Buyer #', 'Email', 'VIN', 'Vehicle', 'Seller'].join('\t'),
        ...buyers.flatMap((b) => b.vins.map((v) => [
          b.buyer.buyer_name ?? '',
          b.buyer.buyer_uid,
          emails[b.buyer.buyer_uid] ?? '',
          v.vin,
          v.label,
          v.seller ?? '',
        ].join('\t'))),
      ].join('\n');
      await navigator.clipboard.writeText(text);
      const missing = buyers.filter((b) => !emails[b.buyer.buyer_uid]).length;
      setContactsMsg(missing ? `Copied — ${missing} without an email` : 'Copied!');
      setTimeout(() => setContactsMsg(null), 2500);
    } catch (err) {
      console.error('Failed to copy basket buyers', err);
      setContactsMsg('Copy failed');
      setTimeout(() => setContactsMsg(null), 2500);
    } finally {
      setCopyingContacts(false);
    }
  };

  /**
   * §2.10 — one Salesforce activity block per VIN, in a single copy.
   *
   * Built from `vinGroups`, so every buyer holding the VIN is listed regardless
   * of how they got there; the old block was assembled from a separate pass and
   * manually assigned buyers were reported missing from it.
   */
  const copySalesforceBlocks = async () => {
    if (vinGroups.length === 0) return;
    try {
      await navigator.clipboard.writeText(toSalesforceBlocks(vinGroups));
      const n = vinGroups.length;
      setContactsMsg(`Copied ${n} note${n !== 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Failed to copy Salesforce blocks', err);
      setContactsMsg('Copy failed');
    }
    setTimeout(() => setContactsMsg(null), 2500);
  };

  /**
   * Copy ONE draft. Outreach goes out per contact, so a combined "copy all" blob
   * was never usable — it just had to be pulled apart again.
   */
  const copyOneDraft = async (r: BatchDraftResult) => {
    try {
      await copyDraftToClipboard(r.subject, r.body, r.vinLinks, r.recipients);
      setBatch((b) => (b ? { ...b, copiedUid: r.id } : b));
      setTimeout(() => setBatch((b) => (b?.copiedUid === r.id ? { ...b, copiedUid: null } : b)), 2000);
    } catch {
      /* clipboard blocked — user can select/copy manually */
    }
  };

  const copyDraft = async () => {
    if (!draft?.result) return;
    try {
      await copyDraftToClipboard(
        draft.result.subject, draft.result.body, draft.result.vin_links ?? {},
        draft.recipients,
      );
      setDraft((d) => (d ? { ...d, copied: true } : d));
    } catch {
      /* clipboard blocked — user can select/copy manually */
    }
  };

  return (
    <div
      className="bb-overlay"
      role="dialog"
      aria-label="Buyer outreach basket"
      style={{ width: `${width}px` }}
    >
      <div
        className="bb-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize basket"
        onMouseDown={startResize}
        title="Drag to resize"
      />
      <div className="bb-header">
        <div className="bb-header-top">
          <div className="bb-title">
            <h3>Outreach Basket</h3>
            <span className="bb-summary">
              {groupBy === 'buyer'
                ? `${buyers.length} buyer${buyers.length !== 1 ? 's' : ''} · ${totalVins} VIN${totalVins !== 1 ? 's' : ''}`
                : groupBy === 'vin'
                ? `${vinGroups.length} VIN${vinGroups.length !== 1 ? 's' : ''} · ${buyers.length} buyer${buyers.length !== 1 ? 's' : ''}`
                : `${sellerGroups.length} seller${sellerGroups.length !== 1 ? 's' : ''} · ${totalVins} VIN${totalVins !== 1 ? 's' : ''}`}
            </span>
          </div>
          <button className="bb-close" onClick={onClose} aria-label="Close basket">✕</button>
        </div>
        {(buyers.length > 0 || contactsMsg) && (
        <div className="bb-header-actions">
          {buyers.length > 0 && groupBy === 'buyer' && (
            <button
              className="bb-batch-btn"
              onClick={runBatchDrafts}
              disabled={!!batch?.running}
              title="Generate a draft for every buyer in the basket"
            >
              {batch?.running ? `Drafting ${batch.done}/${batch.total}…` : `✉ Draft all (${buyers.length})`}
            </button>
          )}
          {vinGroups.length > 0 && groupBy === 'vin' && (
            <button
              className="bb-batch-btn"
              onClick={runVinBatchDrafts}
              disabled={!!batch?.running}
              title="Generate one generic email per VIN, to send to that VIN's buyers"
            >
              {batch?.running ? `Drafting ${batch.done}/${batch.total}…` : `✉ Draft all (${vinGroups.length})`}
            </button>
          )}
          {buyers.length > 0 && groupBy !== 'seller' && (
            <button
              className="bb-batch-btn"
              onClick={runGenericDrafts}
              disabled={!!batch?.running}
              title="One generic email per group of buyers, covering all their VINs — no buyer appears twice"
            >
              ✉ Generic emails
            </button>
          )}
          {buyers.length > 0 && (
            <>
              <button
                className="bb-batch-btn"
                onClick={copyBuyerBlock}
                disabled={copyingContacts}
                title="Copy every basket buyer with their account number, email and staged VINs (tab-separated, pastes into Sheets/Excel)"
              >
                {copyingContacts ? 'Copying…' : `⧉ Copy buyers (${buyers.length})`}
              </button>
              <button
                className="bb-batch-btn"
                onClick={copySalesforceBlocks}
                title="Copy one Salesforce activity block per VIN, each listing the buyers it went to"
              >
                ⧉ Salesforce ({vinGroups.length})
              </button>
            </>
          )}
          {contactsMsg && <span className="bb-summary">{contactsMsg}</span>}
          {buyers.length > 0 && (
            <button className="bb-clear" onClick={onClear}>Clear</button>
          )}
        </div>
        )}
      </div>

      {buyers.length > 0 && (
        <div className="bb-groupby" role="group" aria-label="Group basket by">
          <span className="bb-groupby-label">Group by</span>
          <div className="bb-groupby-toggle">
            <button
              type="button"
              className={`bb-groupby-btn${groupBy === 'buyer' ? ' is-active' : ''}`}
              aria-pressed={groupBy === 'buyer'}
              onClick={() => setGroupBy('buyer')}
            >Buyer</button>
            <button
              type="button"
              className={`bb-groupby-btn${groupBy === 'seller' ? ' is-active' : ''}`}
              aria-pressed={groupBy === 'seller'}
              onClick={() => setGroupBy('seller')}
            >Seller</button>
            <button
              type="button"
              className={`bb-groupby-btn${groupBy === 'vin' ? ' is-active' : ''}`}
              aria-pressed={groupBy === 'vin'}
              onClick={() => setGroupBy('vin')}
            >VIN</button>
          </div>
        </div>
      )}

      <div className="bb-body">
        {buyers.length === 0 ? (
          <div className="bb-empty">
            Add buyers from a VIN's recommendations to build a per-contact outreach list.
          </div>
        ) : groupBy === 'seller' ? (
          sellerGroups.map((g) => (
            <div key={g.key} className="bb-buyer">
              <div className="bb-buyer-head">
                <div className="bb-buyer-id">
                  {onOpenSeller && (g.seller || g.sellerId) ? (
                    <button
                      type="button"
                      className="bb-link bb-buyer-name"
                      onClick={() => onOpenSeller(g.seller, g.sellerId)}
                      title="Open this seller"
                    >{g.seller || `Seller ${g.sellerId ?? g.key}`}</button>
                  ) : (
                    <span className="bb-buyer-name">{g.seller || `Seller ${g.sellerId ?? g.key}`}</span>
                  )}
                  <span className="bb-buyer-meta">
                    {[g.sellerId ? `#${g.sellerId}` : null, g.sellerLoc].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <button
                  className="bb-draft-btn"
                  onClick={() => runDraft('seller', g.seller || `Seller ${g.sellerId ?? g.key}`, {
                    mode: 'seller',
                    seller_id: g.sellerId,
                    seller_name: g.seller,
                    vins: g.items.map((it) => it.vin),
                  })}
                  title="Generate a seller outreach email you can copy/paste"
                >✉ Draft email</button>
              </div>
              <ul className="bb-vin-list">
                {g.items.map((it) => (
                  <li key={`${it.buyer.buyer_uid}:${it.vin}`} className="bb-vin">
                    <span className="bb-vin-info">
                      {onOpenVin ? (
                        <button
                          type="button"
                          className="bb-link bb-vin-label"
                          onClick={() => onOpenVin(it.vin, g.sellerId)}
                          title={`Open VIN details — ${it.vin}`}
                        >{it.label}</button>
                      ) : (
                        <span className="bb-vin-label" title={it.vin}>{it.label}</span>
                      )}
                      {onOpenBuyer ? (
                        <button
                          type="button"
                          className="bb-link bb-vin-seller"
                          onClick={() => onOpenBuyer(it.buyer.buyer_uid, it.buyer.buyer_name)}
                          title="Open this buyer on the Buyer tab"
                        >
                          {['→', it.buyer.buyer_name || `Buyer ${it.buyer.buyer_uid}`,
                            it.buyer.state, it.buyer.buyer_zip_code].filter(Boolean).join(' · ')}
                        </button>
                      ) : (
                        <span className="bb-vin-seller" title="Buyer">
                          {['→', it.buyer.buyer_name || `Buyer ${it.buyer.buyer_uid}`,
                            it.buyer.state, it.buyer.buyer_zip_code].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <button
                      className="bb-remove-vin"
                      onClick={() => onRemoveVin(it.buyer.buyer_uid, it.vin)}
                      aria-label={`Remove ${it.vin}`}
                    >✕</button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        ) : groupBy === 'vin' ? (
          vinGroups.map((g) => (
            <div key={g.vin} className="bb-buyer">
              <div className="bb-buyer-head">
                <div className="bb-buyer-id">
                  {onOpenVin ? (
                    <button
                      type="button"
                      className="bb-link bb-buyer-name"
                      onClick={() => onOpenVin(g.vin, g.sellerId)}
                      title={`Open VIN details — ${g.vin}`}
                    >{g.label}</button>
                  ) : (
                    <span className="bb-buyer-name" title={g.vin}>{g.label}</span>
                  )}
                  <span className="bb-buyer-meta">
                    {[g.seller, g.sellerLoc,
                      `${g.buyers.length} buyer${g.buyers.length !== 1 ? 's' : ''}`]
                      .filter(Boolean).join(' · ')}
                  </span>
                </div>
                <button
                  className="bb-draft-btn"
                  onClick={() => runDraft('generic', g.label, { mode: 'generic', vins: [g.vin] }, g.buyers)}
                  title="Generate one generic email about this VIN for all of its buyers"
                >✉ Draft email</button>
              </div>
              {onAssignVinToBuyer && (
                <BuyerPicker
                  label="+ Add buyer"
                  title="Stage this VIN for another buyer"
                  vin={g.vin}
                  excludeUids={new Set(g.buyers.map((b) => b.buyer_uid))}
                  onPick={(b) => onAssignVinToBuyer(
                    { buyer_uid: b.buyer_uid, buyer_name: b.buyer_name, buyer_zip_code: null, state: null },
                    { vin: g.vin, label: g.label, seller: g.seller, seller_id: g.sellerId, seller_loc: g.sellerLoc },
                  )}
                />
              )}
              <ul className="bb-vin-list">
                {g.buyers.map((b) => (
                  <li key={`${g.vin}:${b.buyer_uid}`} className="bb-vin">
                    <span className="bb-vin-info">
                      {onOpenBuyer ? (
                        <button
                          type="button"
                          className="bb-link bb-vin-label"
                          onClick={() => onOpenBuyer(b.buyer_uid, b.buyer_name)}
                          title="Open this buyer on the Buyer tab"
                        >{b.buyer_name || `Buyer ${b.buyer_uid}`}</button>
                      ) : (
                        <span className="bb-vin-label">{b.buyer_name || `Buyer ${b.buyer_uid}`}</span>
                      )}
                      <span className="bb-vin-seller">
                        {[`#${b.buyer_uid}`, b.state, b.buyer_zip_code].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <button
                      className="bb-remove-vin"
                      onClick={() => onRemoveVin(b.buyer_uid, g.vin)}
                      aria-label={`Remove ${b.buyer_name || b.buyer_uid} from ${g.vin}`}
                    >✕</button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        ) : (
          buyers.map(({ buyer, vins }) => (
            <div key={buyer.buyer_uid} className="bb-buyer">
              <div className="bb-buyer-head">
                <div className="bb-buyer-id">
                  {onOpenBuyer ? (
                    <button
                      type="button"
                      className="bb-link bb-buyer-name"
                      onClick={() => onOpenBuyer(buyer.buyer_uid, buyer.buyer_name)}
                      title="Open this buyer on the Buyer tab"
                    >{buyer.buyer_name || `Buyer ${buyer.buyer_uid}`}</button>
                  ) : (
                    <span className="bb-buyer-name">{buyer.buyer_name || `Buyer ${buyer.buyer_uid}`}</span>
                  )}
                  <span className="bb-buyer-meta">
                    {[`#${buyer.buyer_uid}`, buyer.state, buyer.buyer_zip_code].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className="bb-buyer-head-actions">
                  <button
                    className="bb-draft-btn"
                    onClick={() => runDraft('buyer', buyer.buyer_name || `Buyer ${buyer.buyer_uid}`, {
                      mode: 'buyer',
                      buyer_uid: buyer.buyer_uid,
                      buyer_name: buyer.buyer_name,
                      vins: vins.map((v) => v.vin),
                    }, [buyer])}
                    title="Generate a buyer outreach email you can copy/paste"
                  >✉ Draft email</button>
                  <button
                    className="bb-remove-buyer"
                    onClick={() => onRemoveBuyer(buyer.buyer_uid)}
                    title="Remove buyer"
                  >Remove</button>
                </div>
              </div>
              <ul className="bb-vin-list">
                {vins.map((v) => (
                  <li key={v.vin} className="bb-vin">
                    <span className="bb-vin-info">
                      {onOpenVin ? (
                        <button
                          type="button"
                          className="bb-link bb-vin-label"
                          onClick={() => onOpenVin(v.vin, v.seller_id)}
                          title={`Open VIN details — ${v.vin}`}
                        >{v.label}</button>
                      ) : (
                        <span className="bb-vin-label" title={v.vin}>{v.label}</span>
                      )}
                      {(v.seller || v.seller_id || v.seller_loc) && (
                        onOpenSeller && (v.seller || v.seller_id) ? (
                          <button
                            type="button"
                            className="bb-link bb-vin-seller"
                            onClick={() => onOpenSeller(v.seller, v.seller_id)}
                            title="Open this seller"
                          >
                            {[v.seller, v.seller_id ? `#${v.seller_id}` : null, v.seller_loc]
                              .filter(Boolean)
                              .join(' · ')}
                          </button>
                        ) : (
                          <span className="bb-vin-seller" title="Seller">
                            {[v.seller, v.seller_id ? `#${v.seller_id}` : null, v.seller_loc]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        )
                      )}
                      {(() => {
                        const mi = distances.get(
                          `${buyer.buyer_uid}:${v.vin.trim().toUpperCase()}`,
                        );
                        return mi == null ? null : (
                          <span
                            className="bb-vin-distance"
                            title={`${Math.round(mi)} miles from ${buyer.buyer_name ?? 'this buyer'} to this vehicle`}
                          >
                            {Math.round(mi)} mi
                          </span>
                        );
                      })()}
                    </span>
                    <span className="bb-vin-actions">
                      {onAssignVinToBuyer && (
                        <BuyerPicker
                          label="+ buyer"
                          className="bb-vin-addbuyer"
                          title="Also stage this VIN for another buyer already in the basket"
                          candidates={buyers.map((x) => x.buyer)}
                          excludeUids={new Set(
                            (buyersByVin.get(v.vin.trim().toUpperCase()) ?? []).map((b) => b.buyer_uid),
                          )}
                          onPick={(b) => onAssignVinToBuyer(
                            { buyer_uid: b.buyer_uid, buyer_name: b.buyer_name, buyer_zip_code: null, state: null },
                            v,
                          )}
                        />
                      )}
                      <button
                        className="bb-remove-vin"
                        onClick={() => onRemoveVin(buyer.buyer_uid, v.vin)}
                        aria-label={`Remove ${v.vin}`}
                      >✕</button>
                    </span>
                  </li>
                ))}
              </ul>
              {onAddSuggestion && (
                <BasketSuggestions
                  buyer={buyer}
                  stagedVins={vins}
                  buyersByVin={buyersByVin}
                  onAdd={onAddSuggestion}
                  onOpenVin={onOpenVin}
                />
              )}
            </div>
          ))
        )}
      </div>

      {buyers.length > 0 && (
        <div className="bb-footer">
          <span className="bb-footer-note">
            {groupBy === 'vin' ? (
              <>Use <strong>✉ Draft email</strong> on a VIN for one generic email to all of its buyers. Automated sending is a later phase.</>
            ) : (
              <>Use <strong>✉ Draft email</strong> on a {groupBy === 'seller' ? 'seller' : 'buyer'} to generate copy for outreach. Automated sending is a later phase.</>
            )}
          </span>
        </div>
      )}

      {draft?.open && (
        <div className="bb-draft-modal" role="dialog" aria-label="Outreach draft">
          <div className="bb-draft-card">
            <div className="bb-draft-head">
              <div className="bb-draft-title">
                <strong>{draft.mode === 'buyer' ? 'Buyer' : 'Seller'} outreach draft</strong>
                <span className="bb-draft-sub">{draft.title}</span>
              </div>
              <button className="bb-close" onClick={() => setDraft(null)} aria-label="Close draft">✕</button>
            </div>
            <div className="bb-draft-body">
              {draft.loading ? (
                <div className="bb-draft-loading">Generating draft…</div>
              ) : draft.error ? (
                <div className="bb-draft-error">{draft.error}</div>
              ) : draft.result ? (
                <>
                  {draft.recipients && (
                    <div className="bb-draft-recipients">
                      <span className="bb-draft-label">Send to</span>
                      {draft.recipients.emails.length > 0 ? (
                        <textarea
                          className="bb-draft-to"
                          value={draft.recipients.emails.join('; ')}
                          rows={2}
                          readOnly
                          aria-label={`Recipients for ${draft.title}`}
                        />
                      ) : (
                        <div className="bb-draft-warning-note">No emails on file for this group.</div>
                      )}
                      {draft.recipients.noEmail.length > 0 && (
                        <div className="bb-draft-warning-note">
                          No email on file — contact manually:{' '}
                          {draft.recipients.noEmail
                            .map((b) => `${b.buyer_uid}${b.buyer_name ? ` (${b.buyer_name})` : ''}`)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                  <PriceWarnings warnings={draft.result.validation_warnings} />
                  {draft.result.subject != null && (
                    <div className="bb-draft-subject">
                      <label className="bb-draft-label" htmlFor="bb-draft-subject">Subject</label>
                      <input
                        id="bb-draft-subject"
                        className="bb-draft-subject-input"
                        type="text"
                        value={draft.result.subject}
                        onChange={(e) => {
                          const subject = e.target.value;
                          setDraft((d) => (d?.result ? { ...d, result: { ...d.result, subject } } : d));
                        }}
                      />
                    </div>
                  )}
                  <textarea
                    className="bb-draft-text"
                    value={draft.result.body}
                    rows={14}
                    aria-label="Outreach draft body"
                    onChange={(e) => {
                      const body = e.target.value;
                      setDraft((d) => (d?.result ? { ...d, result: { ...d.result, body } } : d));
                    }}
                  />
                  <div className="bb-draft-hint">AI-generated from this VIN's data — edit here, then copy. Review before sending.</div>
                </>
              ) : null}
            </div>
            {draft.result && !draft.loading && !draft.error && (
              <div className="bb-draft-actions">
                <button className="bb-draft-copy" onClick={copyDraft}>
                  {draft.copied ? '✓ Copied' : 'Copy'}
                </button>
                <button className="bb-draft-done" onClick={() => setDraft(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {batch && (batch.running || batch.results.length > 0) && (
        <div className="bb-draft-modal" role="dialog" aria-label="Batch outreach drafts">
          <div className="bb-draft-card">
            <div className="bb-draft-head">
              <div className="bb-draft-title">
                <strong>{batch.title}</strong>
                <span className="bb-draft-sub">
                  {batch.running ? `Generating ${batch.done} of ${batch.total}…` : `${batch.results.length} drafts`}
                </span>
              </div>
              {!batch.running && (
                <button className="bb-close" onClick={() => setBatch(null)} aria-label="Close drafts">✕</button>
              )}
            </div>
            <div className="bb-draft-body">
              {batch.running && <div className="bb-draft-loading">Generating drafts…</div>}
              {!batch.running && batch.overlap.length > 0 && (
                <div className="bb-draft-warning" role="status">
                  <strong>
                    {batch.overlap.length} buyer{batch.overlap.length !== 1 ? 's' : ''} will get more than one of these emails
                  </strong>
                  <ul className="bb-draft-warning-list">
                    {batch.overlap.map((o) => (
                      <li key={o.buyer.buyer_uid}>
                        {o.buyer.buyer_name || `Buyer ${o.buyer.buyer_uid}`} — {o.vins.length} VINs
                      </li>
                    ))}
                  </ul>
                  <span className="bb-draft-warning-note">
                    Skip their duplicates, or use Draft all from the Buyer view to reach each buyer once.
                  </span>
                </div>
              )}
              {batch.results.map((r) => {
                const isOpen = openDraftUid === r.id;
                return (
                  <div key={r.id} className="bb-batch-item">
                    <div className="bb-batch-head">
                      <button
                        type="button"
                        className="bb-batch-name"
                        aria-expanded={isOpen}
                        onClick={() => setOpenDraftUid(isOpen ? null : r.id)}
                      >
                        {isOpen ? '▾' : '▸'} {r.name}
                      </button>
                      {(r.priceWarnings?.length ?? 0) > 0 && (
                        <span
                          className="bb-batch-flag"
                          title="Pricing statements in this draft don't match the vehicle data — open it to review"
                        >
                          ⚠ Check pricing
                        </span>
                      )}
                      {!r.error && (
                        <button
                          type="button"
                          className="bb-batch-copy"
                          onClick={() => copyOneDraft(r)}
                          title={`Copy the ${r.name} draft`}
                        >
                          {batch.copiedUid === r.id ? '✓ Copied' : 'Copy'}
                        </button>
                      )}
                    </div>
                    {r.error ? (
                      <div className="bb-draft-error">{r.error}</div>
                    ) : isOpen && (
                      <>
                        {r.recipients && (
                          <div className="bb-draft-recipients">
                            <span className="bb-draft-label">Send to</span>
                            {r.recipients.emails.length > 0 ? (
                              <textarea
                                className="bb-draft-to"
                                value={r.recipients.emails.join('; ')}
                                rows={2}
                                readOnly
                                aria-label={`Recipients for ${r.name}`}
                              />
                            ) : (
                              <div className="bb-draft-warning-note">No emails on file for this group.</div>
                            )}
                            {r.recipients.noEmail.length > 0 && (
                              <div className="bb-draft-warning-note">
                                No email on file — contact manually:{' '}
                                {r.recipients.noEmail
                                  .map((b) => `${b.buyer_uid}${b.buyer_name ? ` (${b.buyer_name})` : ''}`)
                                  .join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                        {(r.unpersonalized?.length ?? 0) > 0 && (
                          <div className="bb-draft-warning-note">
                            Nothing on file linking this buyer to{' '}
                            {r.unpersonalized!.length === 1 ? 'this VIN' : `${r.unpersonalized!.length} of these VINs`}
                            {' '}({r.unpersonalized!.map((v) => `…${v.slice(-6)}`).join(', ')}), so
                            {' '}{r.unpersonalized!.length === 1 ? 'it reads' : 'they read'} generically.
                          </div>
                        )}
                        <PriceWarnings warnings={r.priceWarnings} />
                        {r.subject != null && (
                          <div className="bb-draft-subject">
                            <span className="bb-draft-label">Subject</span>
                            <input
                              className="bb-draft-subject-input"
                              type="text"
                              value={r.subject}
                              aria-label={`Subject for ${r.name}`}
                              onChange={(e) => {
                                const subject = e.target.value;
                                setBatch((b) => (b ? {
                                  ...b,
                                  results: b.results.map((x) => (
                                    x.id === r.id ? { ...x, subject } : x
                                  )),
                                } : b));
                              }}
                            />
                          </div>
                        )}
                        <textarea
                          className="bb-draft-text"
                          value={r.body}
                          rows={10}
                          aria-label={`Draft body for ${r.name}`}
                          onChange={(e) => {
                            const body = e.target.value;
                            setBatch((b) => (b ? {
                              ...b,
                              results: b.results.map((x) => (
                                x.id === r.id ? { ...x, body } : x
                              )),
                            } : b));
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
              {!batch.running && (
                <div className="bb-draft-hint">AI-generated — review and edit before sending. Copy each draft as you send it.</div>
              )}
            </div>
            {!batch.running && (
              <div className="bb-draft-actions">
                <button className="bb-draft-done" onClick={() => setBatch(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
